import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircle2Icon, Loader2Icon, OctagonAlertIcon } from 'lucide-react';
import { useAccount } from '../../contexts/AccountContext';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { TextInput } from '../../components/ui/TextInput';
import { SelectInput } from '../../components/ui/SelectInput';
import { Toggle } from '../../components/ui/Toggle';
import { QuotaMeter } from '../../components/QuotaMeter';
import { formatDate, formatInterval } from '../../utils/format';
import { daysFromNow } from '../../utils/datetime';
const domainPattern = /^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
type Verification = {
  state: 'idle';
} | {
  state: 'checking';
} | {
  state: 'found';
  issuer: string;
  validTo: string;
  altNames: string[];
} | {
  state: 'failed';
  reason: string;
};
export function MonitorNew() {
  const navigate = useNavigate();
  const {
    plan,
    defaults,
    recipients,
    servicesUsed,
    serviceLimit,
    addMonitor
  } = useAccount();
  const [domain, setDomain] = useState('');
  const [label, setLabel] = useState('');
  const [verification, setVerification] = useState<Verification>({
    state: 'idle'
  });
  const [sslEnabled, setSslEnabled] = useState(true);
  const [uptimeEnabled, setUptimeEnabled] = useState(true);
  const [warningWindow, setWarningWindow] = useState(plan.warningWindowFixed ?? defaults.warningWindowDays);
  const [cadence, setCadence] = useState<number[]>(defaults.reminderCadence);
  const [pingInterval, setPingInterval] = useState(plan.pingOptions[plan.pingOptions.length - 1]);
  const [method, setMethod] = useState<'GET' | 'HEAD' | 'POST'>('GET');
  const [expectedStatus, setExpectedStatus] = useState(200);
  const [timeoutSeconds, setTimeoutSeconds] = useState(10);
  const [failureThreshold, setFailureThreshold] = useState(defaults.failureThreshold);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const pending = (sslEnabled ? 1 : 0) + (uptimeEnabled ? 1 : 0);
  const remaining = serviceLimit - servicesUsed;
  const sslBlocked = !sslEnabled && pending >= remaining;
  const uptimeBlocked = !uptimeEnabled && pending >= remaining;
  function verify() {
    if (!domainPattern.test(domain.trim())) {
      setErrors({
        domain: 'Enter a hostname like acme.com.'
      });
      return;
    }
    setErrors({});
    setVerification({
      state: 'checking'
    });
    window.setTimeout(() => {
      const clean = domain.trim().toLowerCase();
      if (clean.includes('legacy') || clean.endsWith('.test')) {
        setVerification({
          state: 'failed',
          reason: 'The server closed the connection before presenting a certificate. You can still add it for uptime monitoring.'
        });
        return;
      }
      setVerification({
        state: 'found',
        issuer: "Let's Encrypt R11",
        validTo: daysFromNow(84),
        altNames: [clean, `www.${clean}`]
      });
    }, 1100);
  }
  function save(event: React.FormEvent) {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!domainPattern.test(domain.trim())) {
      next.domain = 'Enter a hostname like acme.com.';
    }
    if (pending === 0) {
      next.checks = 'Switch on at least one check, or there is nothing to do.';
    }
    if (pending > remaining) {
      next.checks = `Only ${remaining} ${remaining === 1 ? 'service is' : 'services are'} free on ${plan.name}.`;
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      return;
    }
    const id = addMonitor({
      domain: domain.trim().toLowerCase(),
      label: label.trim(),
      sslEnabled,
      uptimeEnabled,
      warningWindowDays: warningWindow,
      reminderCadence: cadence,
      pingIntervalMinutes: pingInterval,
      method,
      expectedStatus,
      timeoutSeconds,
      failureThreshold,
      recipientIds: recipients.filter(r => r.verified).map(r => r.id)
    });
    navigate(`/app/monitors/${id}`);
  }
  const windowOptions = (() => {
    if (plan.warningWindowFixed) {
      return [{
        value: String(plan.warningWindowFixed),
        label: `${plan.warningWindowFixed} days (fixed on ${plan.name})`
      }];
    }
    const [min, max] = plan.warningWindowRange ?? [1, 60];
    return [7, 14, 21, 30, 45, 60, 90].filter(days => days >= min && days <= max).map(days => ({
      value: String(days),
      label: `${days} days`
    }));
  })();
  return <div className="space-y-5" data-id="element-1041" data-mp="3opn58wiharov">
      <Link to="/app" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-snap hover:text-ink" data-id="element-1042" data-mp="3oolku1fkosuh">
        <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1043" data-mp="2da8bs2phahyi" />
        All monitors
      </Link>

      <div data-id="element-1044" data-mp="st8m9h3psja7">
        <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-1045" data-mp="stifddk236k4">
          Add a domain
        </h1>
        <p className="mt-1 text-[13px] text-ink-muted" data-id="element-1046" data-mp="xsbs33riqtj8">
          Each check you switch on spends one of your services.
        </p>
      </div>

      <form onSubmit={save} className="grid gap-5 lg:grid-cols-[1.6fr_0.9fr]" noValidate data-id="element-1047" data-mp="16ju5j6x2yv8m">
        <div className="space-y-5" data-id="element-1048" data-mp="16lotku3nf3gx">
          <Panel data-id="element-1049" data-mp="1bjqab8dlqqp8">
            <PanelHeader title="The domain" description="Just the hostname — no protocol, no path." data-id="element-1050" data-mp="1bi5fdhndl5qu" />
            <div className="space-y-4 px-5 py-5" data-id="element-1051" data-mp="1gjuup9hms5kq">
              <Field label="Domain" htmlFor="new-domain" error={errors.domain} data-id="element-1052" data-mp="1li63ri03qc1w">
                <div className="flex gap-2" data-id="element-1053" data-mp="1pj81fuedeg1r">
                  <TextInput id="new-domain" placeholder="acme.com" value={domain} invalid={Boolean(errors.domain)} onChange={event => {
                  setDomain(event.target.value);
                  setVerification({
                    state: 'idle'
                  });
                }} data-id="element-1054" data-mp="1phx0018edkdt" />
                  <Button variant="secondary" onClick={verify} disabled={verification.state === 'checking'} data-id="element-1055" data-mp="ti2pq9ccp5f3">
                    {verification.state === 'checking' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1056" data-mp="1he6zddhz39qh" />}
                    {verification.state === 'checking' ? 'Looking' : 'Verify'}
                  </Button>
                </div>
              </Field>

              {verification.state === 'checking' && <div className="rounded-control border border-line bg-canvas px-4 py-3" data-id="element-1057" data-mp="204y8qf1yn04v">
                  <div className="skeleton h-3 w-52" data-id="element-1058" data-mp="202lqs1w7cprt" />
                  <div className="skeleton mt-2 h-2.5 w-36" data-id="element-1059" data-mp="1v76g1ithr9zm" />
                </div>}

              {verification.state === 'found' && <div className="flex items-start gap-2.5 rounded-control border border-healthy-border bg-healthy-soft px-4 py-3" role="status" data-id="element-1060" data-mp="2jqq9rrczh1eh">
                  <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-healthy" aria-hidden="true" data-id="element-1061" data-mp="29skazimh4bdu" />
                  <div data-id="element-1062" data-mp="1vzkt59z7y534">
                    <p className="text-[13px] font-medium text-healthy-ink" data-id="element-1063" data-mp="1vzch1kax73sz">
                      Certificate found, valid until{' '}
                      {formatDate(verification.validTo)}
                    </p>
                    <p className="mt-1 text-xs text-healthy-ink/85" data-id="element-1064" data-mp="1r1t5wt9ir759">
                      Issued by {verification.issuer} · covers{' '}
                      {verification.altNames.join(', ')}
                    </p>
                  </div>
                </div>}

              {verification.state === 'failed' && <div className="flex items-start gap-2.5 rounded-control border border-critical-border bg-critical-soft px-4 py-3" role="status" data-id="element-1065" data-mp="1xictsm3re33h">
                  <OctagonAlertIcon className="mt-0.5 h-4 w-4 shrink-0 text-critical" aria-hidden="true" data-id="element-1066" data-mp="1nk6so6ps10zo" />
                  <div data-id="element-1067" data-mp="1ek9xdm8vt3vn">
                    <p className="text-[13px] font-medium text-critical-ink" data-id="element-1068" data-mp="1ekte4zmspm83">
                      No certificate could be read
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-critical-ink/85" data-id="element-1069" data-mp="19s0ayg6z6ly4">
                      {verification.reason}
                    </p>
                  </div>
                </div>}

              <Field label="Label" htmlFor="new-label" hint="Optional. Helps when you have a dozen subdomains." data-id="element-1070" data-mp="vu28o95xifyz">
                <TextInput id="new-label" placeholder="Marketing site" value={label} onChange={event => setLabel(event.target.value)} data-id="element-1071" data-mp="qx22zzczrf8e" />
              </Field>
            </div>
          </Panel>

          <Panel data-id="element-1072" data-mp="3bjilrd7otm89">
            <PanelHeader title="Certificate expiry" description="One inspection a day, and an email on each threshold you pick." action={<Toggle checked={sslEnabled} onChange={() => setSslEnabled(value => !value)} disabled={sslBlocked} label="Monitor certificate expiry" data-id="element-1074" data-mp="2jtnwthnqryk4" />} data-id="element-1073" data-mp="3bgwdfbcz32oz" />
            {sslBlocked && <p className="border-b border-line bg-warn-soft px-5 py-2.5 text-xs text-warn-ink" data-id="element-1075" data-mp="2xkiwq4g9j5bk">
                No services left on {plan.name} for this check.{' '}
                <Link to="/app/billing" className="font-medium underline decoration-1 underline-offset-2" data-id="element-1076" data-mp="251vgp3g4ighc">
                  Move up a plan
                </Link>
                .
              </p>}
            {sslEnabled && <div className="space-y-5 px-5 py-5" data-id="element-1077" data-mp="8bjxjqtcxic">
                <Field label="Warning window" htmlFor="new-window" data-id="element-1078" data-mp="6s23opieeeg">
                  <SelectInput id="new-window" value={String(warningWindow)} disabled={Boolean(plan.warningWindowFixed)} onChange={event => setWarningWindow(Number(event.target.value))} options={windowOptions} data-id="element-1079" data-mp="575zmcthi5y8" />
                </Field>

                <div data-id="element-1080" data-mp="3ifsl9gn74rrw">
                  <p className="text-[13px] font-medium text-ink" data-id="element-1081" data-mp="3igas177hioe2">
                    Reminder thresholds
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-1082" data-mp="2pxnce7b9c1kd">
                    {[30, 21, 14, 7, 3, 1].map(days => {
                  const active = cadence.includes(days);
                  return <button key={days} type="button" aria-pressed={active} onClick={() => setCadence(current => active ? current.filter(d => d !== days) : [...current, days].sort((a, b) => b - a))} className={`tabular rounded-control border px-2.5 py-1.5 text-xs font-medium transition-colors duration-150 ease-snap ${active ? 'border-brand bg-brand-soft text-brand-ink' : 'border-line-strong text-ink-muted hover:border-ink-soft hover:text-ink'}`} data-id="element-1083" data-mp="2df1g5tbvqvsq">
                          {days}d
                        </button>;
                })}
                  </div>
                </div>
              </div>}
          </Panel>

          <Panel data-id="element-1084" data-mp="s574jpbog0mn">
            <PanelHeader title="Uptime" description="A request on your interval, from a monitoring region near you." action={<Toggle checked={uptimeEnabled} onChange={() => setUptimeEnabled(value => !value)} disabled={uptimeBlocked} label="Monitor uptime" data-id="element-1086" data-mp="1ktvzt7q1xbqh" />} data-id="element-1085" data-mp="s5qhermgzytt" />
            {uptimeBlocked && <p className="border-b border-line bg-warn-soft px-5 py-2.5 text-xs text-warn-ink" data-id="element-1087" data-mp="1g12vgkyhw5ex">
                No services left on {plan.name} for this check.{' '}
                <Link to="/app/billing" className="font-medium underline decoration-1 underline-offset-2" data-id="element-1088" data-mp="28i5gjv8er9ar">
                  Move up a plan
                </Link>
                .
              </p>}
            {uptimeEnabled && <div className="grid gap-4 px-5 py-5 sm:grid-cols-2" data-id="element-1089" data-mp="2rmr30wj9bkro">
                <Field label="Ping interval" htmlFor="new-interval" data-id="element-1090" data-mp="2rp4w4zudmmwl">
                  <SelectInput id="new-interval" value={String(pingInterval)} onChange={event => setPingInterval(Number(event.target.value))} options={[30, 15, 5, 1].map(minutes => ({
                value: String(minutes),
                label: plan.pingOptions.includes(minutes) ? formatInterval(minutes) : `${formatInterval(minutes)} — Pro only`,
                disabled: !plan.pingOptions.includes(minutes)
              }))} data-id="element-1091" data-mp="2mp8qyx9y0pfg" />
                </Field>
                <Field label="Request method" htmlFor="new-method" data-id="element-1092" data-mp="1ow0yfss2meqb">
                  <SelectInput id="new-method" value={method} onChange={event => setMethod(event.target.value as typeof method)} options={[{
                value: 'GET',
                label: 'GET'
              }, {
                value: 'HEAD',
                label: 'HEAD'
              }, {
                value: 'POST',
                label: 'POST'
              }]} data-id="element-1093" data-mp="1oxu9dk9pvl44" />
                </Field>
                <Field label="Expected status" htmlFor="new-status" data-id="element-1094" data-mp="2cr3rkcxwr4cl">
                  <TextInput id="new-status" inputMode="numeric" className="tabular" value={String(expectedStatus)} onChange={event => setExpectedStatus(Number(event.target.value.replace(/\D/g, '') || 0))} data-id="element-1095" data-mp="2hkoqc77bs2cu" />
                </Field>
                <Field label="Timeout" htmlFor="new-timeout" hint="Seconds" data-id="element-1096" data-mp="exri28f9lrfz">
                  <TextInput id="new-timeout" inputMode="numeric" className="tabular" value={String(timeoutSeconds)} onChange={event => setTimeoutSeconds(Number(event.target.value.replace(/\D/g, '') || 0))} data-id="element-1097" data-mp="ezm1tjesvdih" />
                </Field>
                <Field label="Failures before alerting" htmlFor="new-threshold" className="sm:col-span-2" hint="A single blip should not wake anyone." data-id="element-1098" data-mp="ttyzj0zagerk">
                  <SelectInput id="new-threshold" value={String(failureThreshold)} onChange={event => setFailureThreshold(Number(event.target.value))} options={[1, 2, 3, 5].map(count => ({
                value: String(count),
                label: `${count} consecutive ${count === 1 ? 'check' : 'checks'}`
              }))} data-id="element-1099" data-mp="1mce50gqs2adr" />
                </Field>
              </div>}
          </Panel>
        </div>

        <div className="space-y-4 lg:sticky lg:top-8 lg:self-start" data-id="element-1100" data-mp="1mp6y3minaymf">
          <QuotaMeter used={servicesUsed} limit={serviceLimit} planName={plan.name} pendingDelta={pending} data-id="element-1101" data-mp="1mqi1vmc3c6dj" />

          <Panel className="p-5" data-id="element-1102" data-mp="1cs27nahrogzt">
            <h2 className="text-sm font-semibold text-ink" data-id="element-1103" data-mp="1ct3l1lm99fkp">
              What this domain will use
            </h2>
            <ul className="mt-3 space-y-2 text-[13px]" data-id="element-1104" data-mp="3751u9nkkebgl">
              <li className="flex items-center justify-between gap-3" data-id="element-1105" data-mp="373qnrfzqipls">
                <span className="text-ink-muted" data-id="element-1106" data-mp="vgx06676cs6j">Certificate expiry</span>
                <span className={sslEnabled ? 'text-ink' : 'text-ink-soft'} data-id="element-1107" data-mp="rg3e7idalnga">
                  {sslEnabled ? '1 service' : 'Off'}
                </span>
              </li>
              <li className="flex items-center justify-between gap-3" data-id="element-1108" data-mp="158t1rr8gz0fi">
                <span className="text-ink-muted" data-id="element-1109" data-mp="10fztitdsl9yo">Uptime</span>
                <span className={uptimeEnabled ? 'text-ink' : 'text-ink-soft'} data-id="element-1110" data-mp="1f5fjw89evddu">
                  {uptimeEnabled ? '1 service' : 'Off'}
                </span>
              </li>
            </ul>
            {errors.checks && <p className="mt-3 text-xs text-critical-ink" data-id="element-1111" data-mp="1o5ay6ly9joi3">{errors.checks}</p>}
            <div className="mt-5 space-y-2" data-id="element-1112" data-mp="1j7146eo9yty2">
              <Button type="submit" size="lg" className="w-full" data-id="element-1113" data-mp="1j56fqqdsojp8">
                Start monitoring
              </Button>
              <Button variant="ghost" size="lg" className="w-full" onClick={() => navigate('/app')} data-id="element-1114" data-mp="3kynndu1xdkjz">
                Cancel
              </Button>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-ink-soft" data-id="element-1115" data-mp="343i51xore5w8">
              Alerts will go to everyone you have confirmed. You can change that
              per domain afterwards.
            </p>
          </Panel>
        </div>
      </form>
    </div>;
}